import { FC, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Divider, Tab } from '@mui/material';

import { Page, PageHeader } from '@/components';
import { Tabs } from '@/components/Tabs';
import { LocalStorageItems } from '@/constants';
import { MasterIndexSection } from '@/pages/MasterIndex/Permissions';
import useMasterIndexAccess from '@/pages/MasterIndex/useMasterIndexAccess';
import { useUserStore } from '@/store';

import { KaseMainContextProvider } from '../Kases/KaseEdit/KaseMainContext';

const sectionToRoute: Record<MasterIndexSection, string> = {
  persons: '/master-index/persons',
  organizations: '/master-index/organizations',
  vehicles: '/master-index/vehicles',
  properties: '/master-index/properties',
  address: '/master-index/address',
  logs: '/master-index/logs',
};

export const MasterIndex: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  const { userAgencyId } = useUserStore();

  const { allowed } = useMasterIndexAccess();

  const tabs = allowed.map((s) => ({
    key: s,
    label:
      s === 'persons'
        ? 'Persons'
        : s === 'organizations'
        ? 'Organizations'
        : s === 'vehicles'
        ? 'Vehicles'
        : s === 'properties'
        ? 'Properties'
        : s === 'address'
        ? 'Address'
        : 'Logs',
    to: sectionToRoute[s],
  }));

  useEffect(() => {
    if (userAgencyId) {
      localStorage.setItem(LocalStorageItems.CurrentAgencyId, userAgencyId);
    }
  }, [userAgencyId]);

  // Hide the tabs if there are more than 3 paths in the URL.
  // We have children routes that does not need tabs.
  const showTabs = paths.length < 4;

  const currentBase = paths.slice(0, 3).join('/');
  const currentTab = tabs.some((t) => t.to === currentBase)
    ? currentBase
    : false;

  const handleTabChange = (_e: any, newValue: string) => navigate(newValue);

  return (
    <KaseMainContextProvider>
      <Page title="Master Index">
        {showTabs && <PageHeader title="Master Index" />}
        {showTabs && (
          <>
            <Tabs value={currentTab} onChange={handleTabChange}>
              {tabs.map((t) => (
                <Tab key={t.key} label={t.label} value={t.to} />
              ))}
            </Tabs>
            <Divider />
          </>
        )}
        <Outlet />
      </Page>
    </KaseMainContextProvider>
  );
};
